import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Gig, EffectiveFeature } from '@stage/contracts';
import { publicEventUrl } from '@stage/config';
import { ApiError } from '../api/client.js';
import { useAdminClient } from '../hooks/useAdminClient.js';
import { lifecycle, statusLabel } from '../lib/lifecycle.js';

/**
 * Gig editor basics (§23.4): shows gig basics, the effective-feature summary and its source, the
 * publish action (surfacing validation field errors from the error envelope, §23.6), lifecycle
 * transitions gated per §13, and the public event URL / QR placement (§14.2). The real QR image is
 * produced by the API / @stage/qr later; here we show the canonical URL and a placeholder.
 */
export function GigEditorPage() {
  const { gigId = '' } = useParams();
  const client = useAdminClient();
  const [gig, setGig] = useState<Gig | null>(null);
  const [features, setFeatures] = useState<EffectiveFeature[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [g, f] = await Promise.all([client.getGig(gigId), client.getFeatures(gigId)]);
      setGig(g);
      setFeatures(f);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load this gig.');
    }
  }, [client, gigId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (label: string, action: () => Promise<Gig>) => {
      setBusy(label);
      setNotice(null);
      if (label === 'publish') setPublishError(null);
      try {
        setGig(await action());
        setNotice(`${label} succeeded.`);
      } catch (err) {
        if (label === 'publish' && err instanceof ApiError) setPublishError(err);
        else setNotice(err instanceof ApiError ? err.message : `${label} failed.`);
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  if (loadError) {
    return (
      <main className="page">
        <p className="error">{loadError}</p>
        <Link className="link" to="/app">
          Back to overview
        </Link>
      </main>
    );
  }
  if (!gig) {
    return (
      <main className="page" aria-busy="true">
        <p>Loading gig…</p>
      </main>
    );
  }

  const eventUrl = publicEventUrl(gig.publicCode);

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            / Gig editor
          </p>
          <h1>{gig.name}</h1>
          <p className="status">
            <span className={`dot status-dot--${gig.status}`} />
            {statusLabel(gig.status)}
          </p>
        </div>
        <div className="button-row">
          {gig.artistId && (
            <Link
              className="button-link"
              to={`/app/artists/${encodeURIComponent(gig.artistId)}/improv-profiles`}
            >
              Improv profiles
            </Link>
          )}
          {gig.artistId && (
            <Link
              className="button-link"
              to={`/app/artists/${encodeURIComponent(gig.artistId)}/rhythm-catalogs`}
            >
              Rhythm catalogs
            </Link>
          )}
          {gig.artistId && (
            <Link
              className="button-link"
              to={`/app/artists/${encodeURIComponent(gig.artistId)}/integrations`}
            >
              Integrations
            </Link>
          )}
          <Link className="button-link" to={`/app/gigs/${gig.gigId}/setlist`}>
            Set list
          </Link>
          {gig.artistId && (
            <Link className="button-link" to={`/app/gigs/${gig.gigId}/tipping`}>
              Tipping
            </Link>
          )}
          <Link className="button-link" to={`/app/gigs/${gig.gigId}/analytics`}>
            Analytics
          </Link>
          <Link className="button-link" to={`/app/gigs/${gig.gigId}/operations`}>
            Operations
          </Link>
          <Link className="button-link button-link--primary" to={`/app/gigs/${gig.gigId}/console`}>
            Open console
          </Link>
        </div>
      </header>

      <section className="card">
        <h2>Basics</h2>
        <dl className="kv">
          <div>
            <dt>Public code</dt>
            <dd>{gig.publicCode}</dd>
          </div>
          <div>
            <dt>Timezone</dt>
            <dd>{gig.timezone}</dd>
          </div>
          <div>
            <dt>Scheduled start</dt>
            <dd>{gig.scheduledStartAt ?? '—'}</dd>
          </div>
          <div>
            <dt>Venue</dt>
            <dd>{gig.venue.publicLabel ?? '—'}</dd>
          </div>
          <div>
            <dt>Published version</dt>
            <dd>{gig.publishedVersion ?? 'Not published'}</dd>
          </div>
        </dl>
      </section>

      <section className="card">
        <div className="module-head">
          <h2>Lifecycle</h2>
          {notice && <span className="muted">{notice}</span>}
        </div>
        <p className="help">
          The API re-checks every transition with optimistic concurrency (§13.3). Ending a gig is
          confirmation-protected.
        </p>
        <div className="button-row">
          <button
            type="button"
            className="action--primary"
            disabled={!lifecycle.canPublish(gig.status) || busy !== null}
            onClick={() => void run('publish', () => client.publishGig(gigId))}
          >
            Publish
          </button>
          <button
            type="button"
            disabled={!lifecycle.canStart(gig.status) || busy !== null}
            onClick={() => void run('start', () => client.startGig(gigId))}
          >
            Start
          </button>
          <button
            type="button"
            disabled={!lifecycle.canIntermission(gig.status) || busy !== null}
            onClick={() => void run('intermission', () => client.intermission(gigId))}
          >
            Intermission
          </button>
          <button
            type="button"
            disabled={!lifecycle.canResume(gig.status) || busy !== null}
            onClick={() => void run('resume', () => client.resumeGig(gigId))}
          >
            Resume
          </button>
          <button
            type="button"
            className="danger"
            disabled={!lifecycle.canEnd(gig.status) || busy !== null}
            onClick={() => {
              if (window.confirm('End this gig? Audience interactions will be disabled.')) {
                void run('end', () => client.endGig(gigId));
              }
            }}
          >
            End
          </button>
        </div>

        {publishError && (
          <div className="validation" role="alert">
            <p className="error">{publishError.message}</p>
            {publishError.fieldErrors.length > 0 && (
              <ul>
                {publishError.fieldErrors.map((fe, i) => (
                  <li key={`${fe.path}-${i}`}>
                    <code>{fe.path}</code>: {fe.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="card">
        <h2>Effective features</h2>
        <p className="help">Resolved values and the scope each came from (§11.2, §23.4).</p>
        {features.length === 0 ? (
          <p className="muted">No feature data.</p>
        ) : (
          <table className="feature-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Enabled</th>
                <th>Source</th>
                <th>Blocked by</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <tr key={f.key}>
                  <td>{f.key}</td>
                  <td>{f.enabled ? 'on' : 'off'}</td>
                  <td>
                    <span className="badge small">{f.source}</span>
                  </td>
                  <td>{f.blockedBy.length > 0 ? f.blockedBy.join(', ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h2>QR codes</h2>
        <p className="help">
          The event QR encodes only this HTTPS URL (§14.2). Print-ready SVG/PNG assets come from the
          service.
        </p>
        <div className="qr-block">
          <div className="qr-placeholder" aria-hidden="true">
            QR
          </div>
          <div>
            <p className="eyebrow small">Public event URL</p>
            <a href={eventUrl} target="_blank" rel="noreferrer">
              {eventUrl}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
